import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import pluralize from 'pluralize';
import { Draggable, DropBin } from '@/components';
import { SUFFIXES } from '@/types';
import { shuffleArray } from '@/utils/array';

export type GrabGameSuffixesSuffixEngine = 'pluralize' | 'correctAnswers';

interface GrabGameSuffixesProps {
  word: string;
  suffixes: Array<string>;
  correctAnswers?: Array<string>;
  choicesLength?: number;
  suffixEngine?: GrabGameSuffixesSuffixEngine;
}

export const GrabGameSuffixes = ({
  word,
  suffixes,
  correctAnswers,
  choicesLength = 3,
  suffixEngine = 'pluralize',
}: GrabGameSuffixesProps) => {
  const [suffixChoices, setSuffixChoices] = useState<Array<string>>([]);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    const resultSet = new Set<string>(suffixes);
    const availableSuffixes = SUFFIXES.filter(
      (suffix) => !resultSet.has(suffix),
    );

    while (resultSet.size < choicesLength && availableSuffixes.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableSuffixes.length);
      const randomSuffix = availableSuffixes.splice(randomIndex, 1)[0];

      resultSet.add(randomSuffix);
    }

    setSuffixChoices(shuffleArray(Array.from(resultSet)));
  }, [suffixes, choicesLength]);

  useEffect(() => {
    if (!value) {
      setIsCorrect(false);

      return;
    }

    switch (suffixEngine) {
      case 'pluralize':
        setIsCorrect(pluralize(word) === `${word}${value}`);

        break;
      case 'correctAnswers':
        setIsCorrect(correctAnswers?.includes(`${word}${value}`) || false);

        break;
      default:
        setIsCorrect(false);
    }
  }, [value, word, correctAnswers, suffixEngine]);

  return (
    <div className="flex flex-col gap-6 justify-center items-center h-full pb-24">
      <div className="flex gap-2 justify-center">
        <h2 className="text-8xl font-sassoonInfant">{word}</h2>

        <DropBin
          className={clsx(
            'h-24 w-36 px-2 font-sassoonInfant text-8xl text-left',
            !value
              ? 'bg-white text-black'
              : isCorrect
                ? 'bg-[var(--c-success)] text-white'
                : 'bg-[var(--c-select)] text-white',
          )}
          value={value}
          setValue={setValue}
          showValue
        />
      </div>

      <div className="flex gap-6 justify-center">
        {suffixChoices.map((suffix, index) => (
          <Draggable
            key={index}
            className="h-24 w-36 bg-white rounded-xl text-8xl font-sassoonInfant"
            value={suffix}
          >
            {suffix}
          </Draggable>
        ))}
      </div>
    </div>
  );
};
